<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1>Comments</h1>
    </div>
    <div class="border-container-unique37">
      <div class="admin-list-title">List of Comments</div>
      <div>You can view all comments on this page.</div>

      <div class="admin-flex-container" ref="adminFlexContainer">
        <div class="admin-search-container">
          <input
            type="text"
            class="admin-search-input"
            placeholder="Search..."
            v-model="searchQuery"
            @input="handleSearchChange"
          />
          <img
            src="/images/maker-cart/union.svg"
            alt="Search"
            class="admin-search-icon"
          />
        </div>
      </div>
      <div class="admin-list-tool-container"></div>

      <div class="mb-3" style="display: flex; gap: 10px; flex-wrap: wrap">
        <button
          v-for="(button, index) in buttons"
          :key="index"
          :class="{ 'checkbox-button-clicked': button.clicked }"
          @click="toggleClicked(index)"
          class="checkbox-button"
        >
          {{ button.label }}
        </button>
      </div>

      <TableData
        :headers="headers"
        :items="tableItems"
        :loading="tableLoading"
        :total-pages="data?.data?.total_pages || 0"
        :current-page="currentPage"
        @update:current-page="changePage"
        :is-action="true"
        :is-view-action="true"
        :is-edit-action="false"
        :is-delete-action="false"
        @action="handleAction"
        :show-data-info="true"
        :total-rows="data?.data?.total_rows || 0"
        :current-limit="filters.limit"
        variant="rounded"
      >
        <template #cell-from="{ value }">
          <span
            style="
              display: inline-block;
              max-width: 200px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            "
            >{{ value }}</span
          >
        </template>
      </TableData>
    </div>
  </div>

  <ModalComponent
    :visible="showCommentModal"
    @update:visible="toggleCommentModal"
  >
    <div class="container-modal" style="min-width: 420px; max-width: 640px">
      <div class="modal-title" style="margin-bottom: 8px">Comment Details</div>
      <div class="border-header" style="margin-bottom: 12px"></div>
      <div
        style="
          display: grid;
          grid-template-columns: 1fr;
          gap: 8px;
          font-size: var(--fs-small);
        "
      >
        <div><strong>Date:</strong> {{ selectedComment?.date_time }}</div>
        <div><strong>User:</strong> {{ selectedComment?.user?.email }}</div>
        <div><strong>From:</strong> {{ selectedComment?.post?.title }}</div>
      </div>
      <div style="margin: 12px 0; font-weight: 600">Comment</div>
      <div
        style="
          max-height: 240px;
          overflow: auto;
          background: #fafafa;
          border: 1px solid #eee;
          border-radius: 8px;
          padding: 10px;
          line-height: 1.5;
        "
      >
        {{ selectedComment?.comment_content }}
      </div>
      <div class="modal-button" style="margin-top: 16px">
        <button class="btn btn-discard" @click="toggleCommentModal">
          Close
        </button>
      </div>
    </div>
  </ModalComponent>
</template>

<script setup>
/* eslint-disable */
import { onMounted, ref, computed, watch } from "vue";
import CommentAdminService from "@/services/commentAdminService";
import ModalComponent from "@/components/ModalComponent.vue";
import { debounce } from "lodash";
import TableData from "@/components/common/TableData.vue";
import { useUrlQuerySync } from "@/composeables/useUrlQuerySync";

const showCommentModal = ref(false);
const selectedComment = ref(null);
const tableLoading = ref(true);
const searchQuery = ref("");
const currentStatus = ref("");
const buttons = ref([
  { label: "All", clicked: true, status: "" },
  { label: "Online Making", clicked: false, status: "lesson" },
  {
    label: "Profesional Development",
    clicked: false,
    status: "instuctional_strategies",
  },
]); // Array untuk state button

const data = ref({
  message: "",
  error: null,
  data: {
    page: 1,
    limit: 10,
    total_rows: 0,
    total_pages: 0,
    rows: [],
  },
});

const currentPage = ref(1);
const expandedIndex = ref(null);
const filters = ref({
  search: "",
  page: 1,
  limit: 10,
  post_type: "",
});

useUrlQuerySync({
  filters,
  excludeDefaults: [{ page: 1 }, { limit: 10 }],
});

onMounted(async () => {
  await getAll();
  window.addEventListener("resize", updateLayout);
  updateLayout();
});

// Define table headers
const headers = ref([
  { text: "Date", value: "date_time", sortable: true },
  { text: "User", value: "user", sortable: false },
  { text: "From", value: "from", sortable: false },
  { text: "Upvote", value: "upvote", sortable: true },
  { text: "Downvote", value: "downvote", sortable: true },
]);

// Map API rows to table items
const tableItems = computed(() => {
  return (data.value?.data?.rows || []).map((item) => ({
    id: item.id,
    date_time: item.date_time || "-",
    user: item.user?.email || "-",
    from: item.post?.title || "-",
    upvote: item.upvote ?? 0,
    downvote: item.downvote ?? 0,
    raw: item,
  }));
});

const getAll = async () => {
  try {
    tableLoading.value = true;

    const response = await CommentAdminService.getAll({
      page: filters.value.page,
      search: filters.value.search,
      post_type: filters.value.post_type,
    });
    if (response.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    console.log(error.message);
  } finally {
    tableLoading.value = false;
  }
};

// Sync search query with filters.search
watch(
  () => filters.value.search,
  (newValue) => {
    searchQuery.value = newValue || "";
  }
);

// Debounced search handler
const handleSearchChange = debounce(() => {
  filters.value.search = searchQuery.value;
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
}, 300);

const changePage = (page) => {
  currentPage.value = page;
  filters.value.page = page;
  getAll();
};

const prevPage = () => {
  if (currentPage.value > 1) {
    changePage(currentPage.value - 1);
  }
};

const nextPage = () => {
  if (currentPage.value < data.value.data.total_pages) {
    changePage(currentPage.value + 1);
  }
};

const visiblePages = computed(() => {
  const total = data.value.data.total_pages;
  const current = currentPage.value;
  const start = Math.max(1, current - 2);
  const end = Math.min(total, current + 2);

  const pages = [];
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return pages;
});

const openModal = (id) => {
  itemId.value = id; // Set itemId
  showModalDelete.value = true;
};

const updateVisibilityModalDelete = () => {
  showModalDelete.value = !showModalDelete.value;
};

const remove = async (id) => {
  isLoading.value = true;
  try {
    await CategoryAdminService.remove(id);
    isLoading.value = false;
    showModalDelete.value = false;
    getAll(currentPage.value, searchQuery.value, currentStatus.value); // Refresh the list
    alert("Category deleted successfully");
  } catch (error) {
    isLoading.value = false;
    console.error(error.message);
    alert("Failed to delete category");
  }
};

// Function to update layout based on window width
const updateLayout = () => {
  const container = document.querySelector(".admin-flex-container");
  const toolContainer = document.querySelector(".admin-list-tool-container");
  const parentContainer = document.querySelector(".border-container-unique37");
  const width = window.innerWidth;

  if (width <= 991) {
    // Memindahkan toolContainer di bawah admin-flex-container
    parentContainer.insertBefore(toolContainer, container.nextSibling);
  } else {
    // Memindahkan toolContainer kembali ke dalam admin-flex-container
    container.appendChild(toolContainer);
    container.insertBefore(toolContainer, container.children[1]);
  }
};

const toggleExpand = (index) => {
  expandedIndex.value = expandedIndex.value === index ? null : index;
};

// Function untuk toggle clicked state
const toggleClicked = (index) => {
  currentPage.value = 1;
  buttons.value.forEach((button, idx) => {
    button.clicked = idx === index;
  });
  currentStatus.value = buttons.value[index].status;
  filters.value.post_type = currentStatus.value;
  getAll();
};

// Handle actions
const handleAction = (action) => {
  if (action.type === "view") {
    selectedComment.value = action.item.raw;
    showCommentModal.value = true;
  }
};

const toggleCommentModal = () => {
  showCommentModal.value = !showCommentModal.value;
};
</script>
